import React, { createContext, useState, useContext } from 'react';
import { productService, orderService } from '../services/api';

// Create shop context
const ShopContext = createContext();

// Shop provider component
export const ShopProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get all products with filters
  const getProducts = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProducts(filters);
      setProducts(response.data.products);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch products');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get product details
  const getProductDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProduct(id);
      setCurrentProduct(response.data.product);
      return response.data.product;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch product details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add product to cart
  const addToCart = (product, quantity = 1) => {
    const existingItem = cart.find(item => item.product.id === product.id);
    
    if (existingItem) {
      // Update quantity if product already in cart
      setCart(cart.map(item => 
        item.product.id === product.id 
          ? { ...item, quantity: item.quantity + quantity } 
          : item
      ));
    } else {
      // Add new item to cart
      setCart([...cart, { product, quantity }]);
    }
  };

  // Update cart item quantity
  const updateCartItemQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.product.id === productId 
          ? { ...item, quantity } 
          : item
      ));
    }
  };

  // Remove product from cart
  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product.id !== productId));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Calculate cart total
  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.product.discountPrice || item.product.price;
      return total + (price * item.quantity);
    }, 0);
  };

  // Create a new order
  const createOrder = async (orderData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.createOrder(orderData);
      setCurrentOrder(response.data.order);
      clearCart(); // Clear cart after successful order
      return response.data.order;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get user orders
  const getUserOrders = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.getOrders(filters);
      setOrders(response.data.orders);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch orders');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get order details
  const getOrderDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.getOrder(id);
      setCurrentOrder(response.data.order);
      return response.data.order;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch order details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cancel an order
  const cancelOrder = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.cancelOrder(id);
      setCurrentOrder(response.data.order);
      return response.data.order;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    products,
    currentProduct,
    cart,
    orders,
    currentOrder,
    loading,
    error,
    getProducts,
    getProductDetails,
    addToCart,
    updateCartItemQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    createOrder,
    getUserOrders,
    getOrderDetails,
    cancelOrder,
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

// Custom hook to use shop context
export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};
